/**
 * AI Message Bubble Component
 * ===========================
 * Displays AI-generated messages with confidence indicators and metadata
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Stack,
  Tooltip,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  SmartToy,
  Timer,
  Build,
  TrendingUp,
  TrendingDown,
  CheckCircle
} from '@mui/icons-material';
import { MarkdownRenderer } from '../MarkdownRenderer';

interface AIMessageBubbleProps {
  content: string;
  confidence?: number;
  toolsUsed?: string[];
  responseTimeMs?: number;
  isStreaming?: boolean;
  timestamp?: Date;
  showMetadata?: boolean;
}

export const AIMessageBubble: React.FC<AIMessageBubbleProps> = ({
  content,
  confidence,
  toolsUsed = [],
  responseTimeMs,
  isStreaming = false,
  timestamp,
  showMetadata = true
}) => {
  // Determine confidence color
  const getConfidenceColor = (conf?: number): 'success' | 'warning' | 'error' | 'default' => {
    if (!conf) return 'default';
    if (conf >= 0.8) return 'success';
    if (conf >= 0.6) return 'warning';
    return 'error';
  };

  // Get confidence icon
  const getConfidenceIcon = (conf?: number): React.ReactElement | undefined => {
    if (!conf) return undefined;
    if (conf >= 0.7) return <TrendingUp fontSize="small" />;
    return <TrendingDown fontSize="small" />;
  };

  // Format response time
  const formatResponseTime = (ms?: number): string => {
    if (!ms) return '';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        mb: 2,
        maxWidth: '80%'
      }}
    >
      <Paper
        elevation={2}
        sx={{
          p: 2,
          backgroundColor: '#f0f4ff',
          borderRadius: 2,
          borderLeft: '4px solid',
          borderLeftColor: isStreaming ? 'primary.main' : 'primary.light',
          position: 'relative',
          width: '100%'
        }}
      >
        {/* Streaming indicator */}
        {isStreaming && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8
            }}
          />
        )}

        {/* Message header with AI indicator */}
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <SmartToy color="primary" fontSize="small" />
          <Typography variant="subtitle2" color="primary" fontWeight={600}>
            AI Assistant
          </Typography>

          {isStreaming && (
            <Chip
              icon={<CircularProgress size={12} color="inherit" />}
              label="Typing..."
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 20 }}
            />
          )}

          {!isStreaming && confidence !== undefined && (
            <Tooltip title={`AI Confidence: ${(confidence * 100).toFixed(0)}%`}>
              <Chip
                icon={getConfidenceIcon(confidence)}
                label={`${(confidence * 100).toFixed(0)}%`}
                size="small"
                color={getConfidenceColor(confidence)}
                variant="outlined"
                sx={{ height: 20 }}
              />
            </Tooltip>
          )}
        </Stack>

        {/* Message content - Render markdown */}
        <MarkdownRenderer
          content={content}
          sx={{
            color: 'text.primary',
            mb: showMetadata && (toolsUsed.length > 0 || responseTimeMs) ? 1.5 : 0
          }}
        />

        {/* Metadata section */}
        {showMetadata && (toolsUsed.length > 0 || responseTimeMs) && (
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
            {/* Tools used indicator */}
            {toolsUsed.length > 0 && (
              <Tooltip title={`Tools used: ${toolsUsed.join(', ')}`}>
                <Chip
                  icon={<Build fontSize="small" />}
                  label={`${toolsUsed.length} tool${toolsUsed.length > 1 ? 's' : ''}`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
              </Tooltip>
            )}

            {/* Response time indicator */}
            {responseTimeMs && (
              <Tooltip title="Response generation time">
                <Chip
                  icon={<Timer fontSize="small" />}
                  label={formatResponseTime(responseTimeMs)}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
              </Tooltip>
            )}

            {/* Delivered indicator */}
            {!isStreaming && (
              <Tooltip title={timestamp ? `Sent at ${timestamp.toLocaleTimeString()}` : 'Delivered'}>
                <Chip
                  icon={<CheckCircle fontSize="small" />}
                  label="Delivered"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
              </Tooltip>
            )}
          </Stack>
        )}

        {/* Timestamp */}
        {timestamp && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}
          >
            {timestamp.toLocaleTimeString()}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default AIMessageBubble;
